import React, { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import PhotosCarousel from "../../metrics/meal-photos/components/PhotosCarousel";

const MealPhotosDisplay = ({ activeDayUtc, mealPhotos, locals }) => {
  const { t } = useTranslation("athlete-portal");
  const [imageErrors, setImageErrors] = useState(new Set());
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showCarousel, setShowCarousel] = useState(false);

  const handleImageError = (photoId) => {
    setImageErrors((prev) => new Set([...prev, photoId]));
  };

  const handlePhotoClick = (photo, mealType) => {
    const allPhotos = getAllPhotos();
    const photoIndex = allPhotos.findIndex((p) => p._id === photo._id);
    const resolvedIndex = photoIndex >= 0 ? photoIndex : 0;
    setSelectedPhotoIndex(resolvedIndex);
    setShowCarousel(true);
    submit("/actions/refresh", {
      method: "GET",
      body: {
        dailyStatId: day._id,
        photoIndex: resolvedIndex,
        photoId: "",
      },
    });
  };

  // Close photo carousel
  const closePhotoCarousel = () => {
    setShowCarousel(false);
    submit("/actions/refresh", {
      method: "GET",
      body: {
        dailyStatId: "",
        photoIndex: "",
      },
    });
  };

  // Handle photo change in carousel
  const handlePhotoChange = (newIndex) => {
    setSelectedPhotoIndex(newIndex);
  };

  const getMealTime = (mealPhotos) => {
    if (!mealPhotos?.list || mealPhotos.list.length === 0) return "";

    try {
      const firstPhoto = mealPhotos.list[0];
      if (!firstPhoto?.timestamp) return "";

      const timestamp = moment(firstPhoto.timestamp);
      if (!timestamp.isValid()) return "";

      return timestamp.format("h:mmA");
    } catch (error) {
      console.warn("Error formatting meal time:", error);
      return "";
    }
  };

  const getNutritionInfo = () => {
    if (day.nutrients) {
      return {
        protein: Math.round(day.nutrients.proteins || 0),
        carbs: Math.round(day.nutrients.carbs || 0),
        fat: Math.round(day.nutrients.fats || 0),
      };
    }

    return null;
  };

  const formatDate = (dateNumber) => {
    try {
      const dateStr = dateNumber.toString();
      if (dateStr.length !== 8) {
        throw new Error("Invalid date format");
      }
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const dayNum = dateStr.substring(6, 8);
      const date = moment(`${year}-${month}-${dayNum}`);

      if (!date.isValid()) {
        throw new Error("Invalid date");
      }

      return date.format("MMM D, YYYY");
    } catch (error) {
      console.warn("Error formatting date:", error);
      return t("nutritionPage.mealPhotos.invalidDate");
    }
  };

  if (!mealPhotos || !mealPhotos.days || mealPhotos.days.length === 0) {
    return (
      <div className="meal-photos-display">
        <div className="empty-state h-full">
          <span className="font-bold">
            {t("nutritionPage.mealPhotos.noPhotosForDay")}
          </span>
        </div>
      </div>
    );
  }

  const day = mealPhotos.days[0];
  const dayMealPhotos = day.mealPhotos || {};
  const nutrition = getNutritionInfo(day);

  // Collect all photos from all meals with additional metadata
  const getAllPhotos = () => {
    const allPhotos = [];

    Object.entries(dayMealPhotos).forEach(([mealType, mealData]) => {
      if (mealData.list && mealData.list.length > 0) {
        mealData.list.forEach((photo) => {
          allPhotos.push({
            ...photo,
            mealType: mealType,
            mealTags: mealData.tags || [],
          });
        });
      }
    });
    return allPhotos;
  };

  return (
    <div className="meal-photos-display">
      <header className="meal-photos-header">
        <div className="date-section">
          <h4>
            <b>{formatDate(day.utcDate)}</b>
          </h4>
        </div>
        {nutrition && (
          <div className="meal-photos-nutrition-info">
            <div className="nutrition-item protein">
              <span>P</span>
              <span>{nutrition.protein}g</span>
            </div>
            <div className="nutrition-item carbs">
              <span>C</span>
              <span>{nutrition.carbs}g</span>
            </div>
            <div className="nutrition-item fat">
              <span>F</span>
              <span>{nutrition.fat}g</span>
            </div>
          </div>
        )}
      </header>

      <div className="meals-container">
        {Object.entries(dayMealPhotos).map(([mealType, mealData]) => (
          <div key={mealType} className="meal-section">
            <div className="meal-header">
              <p>{mealType}</p>
              <span className="meal-time">{getMealTime(mealData)}</span>
            </div>

            {mealData.list && mealData.list.length > 0 && (
              <div
                className="photos-grid"
                role="grid"
                aria-label={`${mealType} photos`}
              >
                {mealData.list.map((photo) => {
                  const hasError = imageErrors.has(photo._id);

                  return (
                    <div key={photo._id} className="photo-item" role="gridcell">
                      {hasError ? (
                        <div
                          className="photo-error"
                          aria-label={t(
                            "nutritionPage.mealPhotos.imageFailedToLoad"
                          )}
                        >
                          <span>📷</span>
                          <span className="sr-only">
                            {t(
                              "nutritionPage.mealPhotos.imageFailedToLoad"
                            )}
                          </span>
                        </div>
                      ) : (
                        <img
                          src={photo.image.s3Path}
                          alt={`${mealType} photo taken at ${moment(
                            photo.timestamp
                          ).format("h:mm A")}`}
                          className="meal-photo"
                          loading="lazy"
                          onClick={() => handlePhotoClick(photo, mealType)}
                          onError={() => handleImageError(photo._id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handlePhotoClick(photo, mealType);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={t(
                            "nutritionPage.mealPhotos.viewPhotoFullSize",
                            { mealType }
                          )}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {(!mealData.list || mealData.list.length === 0) && (
              <div
                className="no-photos"
                aria-label={t("nutritionPage.mealPhotos.noPhotosForMeal")}
              >
                <span className="no-photos-text">
                  {t("nutritionPage.mealPhotos.noPhotosForMeal")}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Photos Carousel */}
      <PhotosCarousel
        photos={getAllPhotos()}
        selectedPhotoIndex={selectedPhotoIndex}
        isOpen={showCarousel}
        onClose={closePhotoCarousel}
        onPhotoChange={handlePhotoChange}
        date={formatDate(day.utcDate)}
        locals={locals}
        utcDate={day.utcDate}
      />
    </div>
  );
};

export default MealPhotosDisplay;
